import React, { useEffect, useMemo, useState } from 'react';
import fileService from '../../services/fileService';

const avatarFileUrlCache = new Map();
const AVATAR_FILE_URL_STORAGE_PREFIX = 'tasklina:avatar-url:';
const AVATAR_FILE_URL_TTL_MS = 10 * 60 * 1000;

function getStoredAvatarUrl(fileId) {
  try {
    const raw = window.localStorage.getItem(`${AVATAR_FILE_URL_STORAGE_PREFIX}${fileId}`);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.url || Date.now() - cached.cachedAt > AVATAR_FILE_URL_TTL_MS) {
      window.localStorage.removeItem(`${AVATAR_FILE_URL_STORAGE_PREFIX}${fileId}`);
      return null;
    }
    return cached.url;
  } catch {
    return null;
  }
}

function storeAvatarUrl(fileId, url) {
  avatarFileUrlCache.set(fileId, url);
  try {
    window.localStorage.setItem(
      `${AVATAR_FILE_URL_STORAGE_PREFIX}${fileId}`,
      JSON.stringify({ url, cachedAt: Date.now() })
    );
  } catch {
    // Memory cache still prevents repeat calls during this session.
  }
}

const Avatar = ({ 
  user, 
  size = 'md', 
  className = '', 
  onClick = null,
  showName = false,
  alt = ''
}) => {
  // Size configurations
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl'
  };

  // Get user's initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
  };

  // Generate consistent color based on user name
  const getBackgroundColor = (name) => {
    if (!name) return 'bg-gray-400';
    
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const userName = user?.name || user?.email || 'Unknown';
  const initials = getInitials(userName);
  const bgColor = getBackgroundColor(userName);
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const profileImageSrc = useMemo(() => {
    const possibleSources = [user?.profileImage, user?.avatar, user?.profilePicture];
    const source = possibleSources.find(Boolean);
    if (!source) return null;

    const normalizedSource = String(source).trim().toLowerCase();
    if (
      normalizedSource.endsWith('default-avatar.jpg') ||
      normalizedSource.includes('/default-avatar') ||
      normalizedSource.includes('default-avatar.')
    ) {
      return null;
    }

    return source;
  }, [user?.profileImage, user?.avatar, user?.profilePicture]);
  const [resolvedFileImage, setResolvedFileImage] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setResolvedFileImage(null);
    if (!profileImageSrc || !String(profileImageSrc).startsWith('file:')) return undefined;

    const fileId = String(profileImageSrc).slice(5);
    const cached = avatarFileUrlCache.get(fileId) || getStoredAvatarUrl(fileId);
    if (cached) {
      avatarFileUrlCache.set(fileId, cached);
      setResolvedFileImage(cached);
      return undefined;
    }

    fileService
      .getDownloadUrl(fileId)
      .then((response) => {
        const url = response?.data?.downloadUrl;
        if (url) storeAvatarUrl(fileId, url);
        if (active) setResolvedFileImage(url || null);
      })
      .catch(() => {
        if (active) setImageLoadFailed(true);
      });

    return () => {
      active = false;
    };
  }, [profileImageSrc]);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [profileImageSrc]);

  const displayImageSrc = String(profileImageSrc || '').startsWith('file:')
    ? resolvedFileImage
    : profileImageSrc;
  const hasProfileImage = Boolean(displayImageSrc) && !imageLoadFailed;

  const avatarElement = (
    <div 
      className={`
        ${sizeClass}
        rounded-full
        flex items-center justify-center
        cursor-pointer
        transition-all duration-200
        hover:opacity-80
        overflow-hidden
        ${className}
      `}
      onClick={onClick}
      title={alt || userName}
    >
      {hasProfileImage ? (
        <img 
          src={displayImageSrc}
          alt={userName} 
          className="w-full h-full object-cover"
          onError={() => setImageLoadFailed(true)}
        />
      ) : (
        <div className={`w-full h-full ${bgColor} flex items-center justify-center font-semibold !text-white`}>
          {initials}
        </div>
      )}
    </div>
  );

  if (showName) {
    return (
      <div className="flex items-center space-x-2">
        {avatarElement}
        <span className="text-sm font-medium text-gray-900 truncate">
          {userName}
        </span>
      </div>
    );
  }

  return avatarElement;
};

export default Avatar;
